/**
 * apps/web/src/components/JobSummaryCard.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Render the job summary as readable rich text without trusting raw HTML.
 *
 * High-level data flow:
 *   - AuditViewerPage passes `detail.jobSummary` from the route loader.
 *   - This component converts plain text, markdown-like text, and simple
 *     HTML fragments into safe markdown before rendering.
 */

import React from 'react';
import { Card, Spoiler, Text, Title } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/** Matches `<br>` tags so explicit HTML line breaks survive the safe text path. */
const SUMMARY_BREAK_TAG_PATTERN = /<br\s*\/?>/gi;

/** Matches closing block tags that should become paragraph spacing. */
const SUMMARY_BLOCK_END_TAG_PATTERN = /<\/(?:div|p|section|article|h[1-6])>/gi;

/** Matches opening list item tags so simple HTML lists remain readable. */
const SUMMARY_LIST_ITEM_OPEN_TAG_PATTERN = /<li[^>]*>/gi;

/** Matches closing list item tags so each list entry ends on its own line. */
const SUMMARY_LIST_ITEM_CLOSE_TAG_PATTERN = /<\/li>/gi;

/** Matches any remaining HTML tags that should be stripped before markdown rendering. */
const SUMMARY_REMAINING_TAG_PATTERN = /<[^>]+>/g;

/** Matches bullet-like unicode characters so they render as markdown list items. */
const SUMMARY_BULLET_PATTERN = /^\s*[•●▪]\s*/gm;

/** Matches repeated blank lines so the output stays compact. */
const SUMMARY_EXTRA_BREAK_PATTERN = /\n{3,}/g;

/** Matches single newlines that should become markdown hard breaks. */
const SUMMARY_SINGLE_LINE_BREAK_PATTERN = /(?<!\n)\n(?!\n)/g;

/** Max collapsed height used before the reader expands a long summary. */
const JOB_SUMMARY_COLLAPSED_HEIGHT = 220;

/** Minimum summary size before we add progressive disclosure controls. */
const JOB_SUMMARY_COLLAPSIBLE_CHAR_THRESHOLD = 500;

/** Decode a small set of common HTML entities before we strip tags safely. */
function decodeSummaryEntities(input: string): string {
  return input
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

/** Convert plain text, markdown-like text, and simple HTML fragments into safe markdown. */
function normalizeSummaryContent(summary: string): string {
  return decodeSummaryEntities(summary)
    .replace(/\r\n?/g, '\n')
    .replace(SUMMARY_BREAK_TAG_PATTERN, '\n')
    .replace(SUMMARY_BLOCK_END_TAG_PATTERN, '\n\n')
    .replace(SUMMARY_LIST_ITEM_OPEN_TAG_PATTERN, '- ')
    .replace(SUMMARY_LIST_ITEM_CLOSE_TAG_PATTERN, '\n')
    .replace(SUMMARY_REMAINING_TAG_PATTERN, '')
    .replace(SUMMARY_BULLET_PATTERN, '- ')
    .replace(SUMMARY_EXTRA_BREAK_PATTERN, '\n\n')
    .trim()
    .replace(SUMMARY_SINGLE_LINE_BREAK_PATTERN, '  \n');
}

/** Render the job summary in a readable rich-text card without injecting raw HTML. */
export function JobSummaryCard({ summary }: { summary?: string }): React.ReactElement {
  const normalizedSummary = summary ? normalizeSummaryContent(summary) : '';

  /** True when the summary is long enough to benefit from a collapsed default state. */
  const isCollapsible = normalizedSummary.length >= JOB_SUMMARY_COLLAPSIBLE_CHAR_THRESHOLD;

  return (
    <Card withBorder radius="md">
      <Title order={4} mb="sm">
        Job Summary
      </Title>

      {normalizedSummary ? (
        isCollapsible ? (
          <Spoiler
            maxHeight={JOB_SUMMARY_COLLAPSED_HEIGHT}
            showLabel="Show full summary"
            hideLabel="Show less"
          >
            <div style={{ overflowWrap: 'anywhere' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {normalizedSummary}
              </ReactMarkdown>
            </div>
          </Spoiler>
        ) : (
          <div style={{ overflowWrap: 'anywhere' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {normalizedSummary}
            </ReactMarkdown>
          </div>
        )
      ) : (
        <Text c="dimmed" size="sm">
          No summary available.
        </Text>
      )}
    </Card>
  );
}
